/**
 * «Поделиться результатом»: a picture of a deal or of a period that a client
 * sends to Telegram or anywhere else (owner, 2026-10-29, after Bybit's and
 * Smart Systems' PnL cards). Drawn in the browser: the figures are the ones
 * the page already shows, and no server renders or stores anything.
 *
 * The amount can be hidden — a client shares the return, not the balance.
 */
import { isNegative, signedMoney, signedPercent, percent } from "@/shared/money/money";
import { humanAge } from "@/shared/time/age";

export type PeriodCard = {
  readonly kind: "period";
  readonly title: string;
  readonly roi: string | null;
  readonly pnl: string;
  readonly deals: number;
  readonly winrate: string | null;
  readonly averageNet: string | null;
};

export type DealCard = {
  readonly kind: "deal";
  readonly symbol: string;
  readonly rate: string;
  readonly net: string;
  readonly heldSeconds: number;
  readonly closedAt: string;
};

export type Card = PeriodCard | DealCard;

export type CardLine = { readonly label: string; readonly value: string };

/** What the card says, in order: one headline and the figures under it. */
export function cardContent(card: Card, hideAmount: boolean): {
  readonly heading: string;
  readonly headline: string;
  readonly negative: boolean;
  readonly lines: readonly CardLine[];
} {
  const amount = (value: string): string => (hideAmount ? "*****" : `${signedMoney(value)} USDT`);
  if (card.kind === "deal") {
    return {
      heading: card.symbol,
      headline: signedPercent(card.rate, 2),
      negative: isNegative(card.rate),
      lines: [
        { label: "Результат", value: amount(card.net) },
        { label: "Длилась", value: humanAge(card.heldSeconds) },
        { label: "Закрыта", value: new Date(card.closedAt).toLocaleDateString("ru-RU") },
      ],
    };
  }
  return {
    heading: card.title,
    headline: card.roi === null ? "—" : signedPercent(card.roi, 2),
    negative: card.roi !== null && isNegative(card.roi),
    lines: [
      { label: "Результат", value: amount(card.pnl) },
      { label: "Сделок", value: String(card.deals) },
      ...(card.winrate === null ? [] : [{ label: "В плюс", value: percent(card.winrate, 0) }]),
      ...(card.averageNet === null ? [] : [{ label: "Средняя сделка", value: amount(card.averageNet) }]),
    ],
  };
}

const WIDTH = 1080;
const HEIGHT = 1350;

/** The card as a PNG. */
export async function cardImage(card: Card, hideAmount: boolean): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext("2d");
  if (context === null) throw new Error("canvas is unavailable");
  const content = cardContent(card, hideAmount);
  // The theme's own colours and face: the card looks like the cabinet it came from.
  const style = getComputedStyle(document.documentElement);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  const primary = token("--color-text-primary");
  const muted = token("--color-text-muted");
  const face = token("--font-sans");
  const font = (weight: number, size: number): string => `${String(weight)} ${String(size)}px ${face}`;
  const background = context.createLinearGradient(0, 0, 0, HEIGHT);
  background.addColorStop(0, token("--color-background-base"));
  background.addColorStop(1, token("--color-surface-default"));
  context.fillStyle = background;
  context.fillRect(0, 0, WIDTH, HEIGHT);

  context.fillStyle = primary;
  context.font = font(600, 56);
  context.fillText("SKRYPTOS", 80, 140);
  context.fillStyle = muted;
  context.font = font(400, 34);
  context.fillText(card.kind === "deal" ? "Сделка" : "Результат за период", 80, 200);

  context.fillStyle = primary;
  context.font = font(600, 64);
  context.fillText(content.heading, 80, 360);
  context.fillStyle = token(content.negative ? "--color-status-negative" : "--color-status-positive");
  context.font = font(700, 180);
  context.fillText(content.headline, 70, 560);

  let y = 720;
  for (const line of content.lines) {
    context.fillStyle = muted;
    context.font = font(400, 38);
    context.fillText(line.label, 80, y);
    context.fillStyle = primary;
    context.font = font(600, 52);
    context.fillText(line.value, 80, y + 64);
    y += 150;
  }

  context.fillStyle = muted;
  context.font = font(400, 32);
  context.fillText("Торговый бот на Bybit · skryptos", 80, HEIGHT - 80);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) reject(new Error("the card could not be drawn"));
      else resolve(blob);
    }, "image/png");
  });
}

export type ShareOutcome = "shared" | "saved" | "cancelled";

/**
 * The phone's own share sheet, where Telegram is; elsewhere the picture is
 * saved and copied, to paste into a chat.
 */
export async function shareImage(image: Blob, name: string): Promise<ShareOutcome> {
  const file = new File([image], name, { type: "image/png" });
  if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
      throw error;
    }
  }
  const url = URL.createObjectURL(image);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": image })]);
  } catch {
    // A browser that will not copy a picture still has it saved.
  }
  return "saved";
}
